import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { CountrySelectorData } from "@hermes/api-model-country";
import { Locale } from "@hermes/locale";
import { LOGGER } from "@hermes/core-web-logger";

// eslint-disable-next-line @typescript-eslint/naming-convention
import * as ShellActions from "../actions/shell.actions";
import { extractActivatedLocalesFromCountrySelectorData } from "../helpers/country-selector.helper";
import { BannerService } from "../services/banner.service";
import { FetchCountriesService } from "../services/fetch-countries.service";
import { FooterService } from "../services/footer.service";

/**
 * Banners side effects.
 */
@Injectable()
export class ShellEffects {
    private readonly actions$ = inject(Actions);
    private readonly bannerService = inject(BannerService);
    private readonly footerService = inject(FooterService);
    private readonly fetchCountriesService = inject(FetchCountriesService);
    private readonly logger = inject(LOGGER);

    public fetchBanners$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShellActions.fetchBanners),
            switchMap(() =>
                this.bannerService.fetchBannersData().pipe(
                    map((response) =>
                        ShellActions.fetchBannersSuccess({ data: response }),
                    ),
                    catchError((error) => {
                        this.logger.debug(error);
                        return of(ShellActions.fetchBannersFailure({ error }));
                    }),
                ),
            ),
        ),
    );

    public fetchFooterItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShellActions.fetchFooterItems),
            switchMap(() =>
                this.footerService.fetchFooterData().pipe(
                    map((response) =>
                        ShellActions.fetchFooterItemsSuccess({
                            data: response,
                        }),
                    ),
                    catchError((error) =>
                        of(ShellActions.fetchFooterItemsFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    public fetchSupportInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShellActions.fetchSupportInfos),
            switchMap(() =>
                this.footerService.fetchSupportInfosData().pipe(
                    map((response) =>
                        ShellActions.fetchSupportInfosSuccess({
                            customerServiceDetail:
                                response.customerServiceDetail,
                            footer: response.footer,
                        }),
                    ),
                    catchError((error) =>
                        of(ShellActions.fetchSupportInfosFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    public fetchCountries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShellActions.fetchCountrySelectorData),
            switchMap(() =>
                this.fetchCountriesService.fetchCountries().pipe(
                    map((countrySelectorData: CountrySelectorData) => {
                        const activatedLocales: Locale[] =
                            extractActivatedLocalesFromCountrySelectorData(
                                countrySelectorData,
                            );
                        return ShellActions.fetchCountrySelectorDataSuccess({
                            countrySelectorData,
                            activatedLocales,
                        });
                    }),

                    catchError((error) => {
                        this.logger.error(
                            `Error encountered while fetching countries: ${error}`,
                        );
                        return of(ShellActions.fetchCountrySelectorDataError());
                    }),
                ),
            ),
        ),
    );
}
